import { useEffect, useState } from "react";
import {
  TEXT_MODEL_OPTIONS, IMAGE_MODEL_OPTIONS, IMAGE_SIZE_OPTIONS, REASONING_EFFORT_OPTIONS, generationPreferencesSchema, publicModelCatalogSchema,
  type GenerationPreferences, type PublicModelCatalog, type TextModel, type ImageModel, type ImageSize, type ReasoningEffort,
} from "@ppt-agent/contracts";

export type ModelCatalogLoader = (signal: AbortSignal) => Promise<PublicModelCatalog>;
export async function loadPublicModelCatalog(signal: AbortSignal): Promise<PublicModelCatalog> {
  const response = await fetch("/api/models", { signal, credentials: "same-origin", cache: "no-store" });
  if (!response.ok) throw new Error("暂时无法获取模型列表");
  const body = await response.json() as { status?: string; artifacts?: { catalog?: unknown } };
  if (body.status !== "ok") throw new Error("模型服务暂不可用");
  return publicModelCatalogSchema.parse(body.artifacts?.catalog);
}
const initialCatalog: PublicModelCatalog = {
  textModels: [...TEXT_MODEL_OPTIONS], imageModels: [...IMAGE_MODEL_OPTIONS],
  reasoningEfforts: [...REASONING_EFFORT_OPTIONS], imageSizes: [...IMAGE_SIZE_OPTIONS],
  defaults: generationPreferencesSchema.parse({}),
};
export function modelPreferencesFromSearch(search: string): GenerationPreferences {
  const params = new URLSearchParams(search);
  const parsed = generationPreferencesSchema.safeParse({
    textModel: params.get("textModel") ?? undefined,
    imageModel: params.get("imageModel") ?? undefined,
    imageSize: params.get("imageSize") ?? undefined,
    reasoningEffort: params.get("reasoningEffort") ?? undefined,
  });
  return parsed.success ? parsed.data : generationPreferencesSchema.parse({});
}
export function useModelSelection(loader?: ModelCatalogLoader, initial?: GenerationPreferences) {
  const [catalog, setCatalog] = useState(initialCatalog);
  const [preferences, setPreferences] = useState(initial ?? initialCatalog.defaults);
  const [loading, setLoading] = useState(Boolean(loader));
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!loader) return;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    void loader(controller.signal).then((value) => {
      if (controller.signal.aborted) return;
      const next = publicModelCatalogSchema.parse(value);
      setCatalog(next);
      setPreferences((current) => ({
        textModel: next.textModels.includes(current.textModel) ? current.textModel : next.defaults.textModel,
        imageModel: next.imageModels.includes(current.imageModel) ? current.imageModel : next.defaults.imageModel,
        imageSize: next.imageSizes.includes(current.imageSize) ? current.imageSize : next.defaults.imageSize,
        reasoningEffort: next.reasoningEfforts.includes(current.reasoningEffort) ? current.reasoningEffort : next.defaults.reasoningEffort,
      }));
    }).catch(() => {
      if (!controller.signal.aborted) setError("模型服务暂不可用，请检查后端后重试。");
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [loader, revision]);
  function update(values: Partial<GenerationPreferences>) {
    setPreferences((current) => {
      const next = { ...current, ...values };
      if (!catalog.reasoningEfforts.includes(next.reasoningEffort)) next.reasoningEffort = catalog.defaults.reasoningEffort;
      if (!catalog.imageSizes.includes(next.imageSize)) next.imageSize = catalog.defaults.imageSize;
      return next;
    });
  }
  return { catalog, preferences, update, loading, error, retry: () => setRevision((value) => value + 1) };
}
const effortLabels: Record<ReasoningEffort, string> = {
  none: "直答", low: "轻思", medium: "均衡", high: "深思", xhigh: "研析", max: "极致",
};
const sizeLabels: Record<ImageSize, string> = {
  auto: "自动构图",
  "1024x1024": "方形 · 1K",
  "1536x1024": "横版 · 1.5K",
  "1024x1536": "竖版 · 1.5K",
  "2048x1152": "横版 · 2K（推荐）",
  "2048x2048": "方形 · 2K",
  "3840x2160": "横版 · 4K",
  "2160x3840": "竖版 · 4K",
};
export function ModelSettings({ selection, className = "" }: { selection: ReturnType<typeof useModelSelection>; className?: string }) {
  const { preferences, catalog, loading, error } = selection;
  return (
    <div className={"model-control " + className}>
      <details className="model-settings">
        <summary>模型 · {loading ? "加载中…" : `${preferences.textModel} · ${effortLabels[preferences.reasoningEffort]} · ${sizeLabels[preferences.imageSize]}`}</summary>
        <div>
          <label>文字模型<select name="textModel" value={preferences.textModel} disabled={loading || Boolean(error)} onChange={(event) => selection.update({ textModel: event.currentTarget.value as TextModel })}>
            {catalog.textModels.map((id) => <option key={id} value={id}>{id}</option>)}
          </select></label>
          <label>图片模型<select name="imageModel" value={preferences.imageModel} disabled={loading || Boolean(error)} onChange={(event) => selection.update({ imageModel: event.currentTarget.value as ImageModel })}>
            {catalog.imageModels.map((id) => <option key={id} value={id}>{id}</option>)}
          </select></label>
          <label>图片尺寸<select name="imageSize" value={preferences.imageSize} disabled={loading || Boolean(error)} onChange={(event) => selection.update({ imageSize: event.currentTarget.value as ImageSize })}>
            {catalog.imageSizes.map((value) => <option key={value} value={value}>{sizeLabels[value]}</option>)}
          </select></label>
          {catalog.reasoningEfforts.length ? <fieldset disabled={loading || Boolean(error)}><legend>推理深度</legend>{catalog.reasoningEfforts.map((value) => <label key={value}>
            <input type="radio" name="reasoningEffort" value={value} checked={value === preferences.reasoningEffort} onChange={() => selection.update({ reasoningEffort: value })} />
            {effortLabels[value]}
          </label>)}</fieldset> : null}
        </div>
      </details>
      {error ? <p className="model-error" role="alert">{error}<button type="button" onClick={selection.retry}>重试</button></p> : null}
    </div>
  );
}
